"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";

/**
 * Hook to manage favicon notification badge.
 * Shows a red dot on the favicon when notifications arrive while the page is hidden.
 * Clears the badge when the page becomes visible.
 */
export function useFaviconNotificationBadge() {
  const $ = _c(6);
  const originalFaviconRef = useRef(null);
  const badgeFaviconRef = useRef(null);
  const isPageVisibleRef = useRef(true);
  const hasUnseenNotificationsRef = useRef(false);
  const getCurrentFavicon = _temp;
  const setFavicon = _temp2;
  const createBadgeFavicon = _temp3;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      ;
      try {
        if (!originalFaviconRef.current) {
          originalFaviconRef.current = getCurrentFavicon();
        }
        if (!originalFaviconRef.current) {
          return;
        }
        if (!badgeFaviconRef.current) {
          badgeFaviconRef.current = await createBadgeFavicon(originalFaviconRef.current);
        }
        setFavicon(badgeFaviconRef.current);
        hasUnseenNotificationsRef.current = true;
      } catch (t1) {
        const error = t1;
        console.error("Failed to show favicon badge:", error);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const showBadge = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (originalFaviconRef.current) {
        setFavicon(originalFaviconRef.current);
      }
      hasUnseenNotificationsRef.current = false;
      badgeFaviconRef.current = null;
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clearBadge = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handleVisibilityChange = () => {
        isPageVisibleRef.current = document.visibilityState === "visible";
        if (isPageVisibleRef.current && hasUnseenNotificationsRef.current) {
          clearBadge();
        }
      };
      isPageVisibleRef.current = document.visibilityState === "visible";
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        if (originalFaviconRef.current) {
          setFavicon(originalFaviconRef.current);
        }
      };
    };
    t3 = [clearBadge, setFavicon];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!isPageVisibleRef.current) {
        showBadge();
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onNotificationReceived = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      onNotificationReceived
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function _temp3(originalUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timeout = setTimeout(() => {
      img.src = "";
      reject(new Error("Favicon image load timed out"));
    }, 5000);
    img.onload = () => {
      clearTimeout(timeout);
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, 32, 32);
      ctx.beginPath();
      ctx.arc(25, 7, 7.5, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(25, 7, 6, 0, Math.PI * 2);
      ctx.fillStyle = "#ef4444";
      ctx.fill();
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error("Failed to load favicon image"));
    };
    img.src = originalUrl;
  });
}
function _temp2(url) {
  let link_0 = document.querySelector("link[rel=\"icon\"], link[rel=\"shortcut icon\"]");
  if (!link_0) {
    link_0 = document.createElement("link");
    link_0.rel = "icon";
    document.head.appendChild(link_0);
  }
  link_0.href = url;
}
function _temp() {
  const link = document.querySelector("link[rel=\"icon\"], link[rel=\"shortcut icon\"]");
  return link?.href ?? null;
}